import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Markdown } from "../components/Markdown";
import { PriorityBadge, StateBadge } from "../components/StateBadge";
import { Button } from "../components/ui/button";
import { DecisionDialog } from "../components/DecisionDialog";
import { RejectHoldDialog } from "../components/RejectHoldDialog";
import { fmtRelative, fmtUSD, fmtECU } from "../lib/format";
import { CheckCircle2, XCircle, MessageSquareDiff, Pause, ChevronRight } from "lucide-react";

// Keep in sync with backend `transitions.CEO_GATED_STATES`.
const CEO_GATED = ["plan_ready", "fix_requested", "changes_requested"];

export default function Approvals() {
  const { data } = useQuery({
    queryKey: ["approvals", "needs-your-call"],
    queryFn: async () =>
      (await api.get("/tasks", { params: { state: CEO_GATED.join(",") } })).data,
    refetchInterval: 15000,
  });
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [decision, setDecision] = useState("approve");
  const [activeId, setActiveId] = useState(null);
  const [rhOpen, setRhOpen] = useState(false);
  const [rhAction, setRhAction] = useState("hold");

  const triggerDecision = (taskId, d) => { setActiveId(taskId); setDecision(d); setOpen(true); };
  const triggerRh = (taskId, action) => { setActiveId(taskId); setRhAction(action); setRhOpen(true); };
  const onDone = () => qc.invalidateQueries({ queryKey: ["approvals", "needs-your-call"] });

  const tasks = data?.tasks || [];
  // Deterministic order: plan_ready first (easiest wins), then fix_requested, then changes_requested.
  const order = { plan_ready: 0, fix_requested: 1, changes_requested: 2 };
  const sorted = [...tasks].sort((a, b) =>
    (order[a.state] ?? 9) - (order[b.state] ?? 9) ||
    (b.updated_at || "").localeCompare(a.updated_at || "")
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
          Needs your call
        </h1>
        <p className="text-sm text-muted-foreground font-mono mt-1" data-testid="approvals-subtitle">
          {sorted.length} item{sorted.length === 1 ? "" : "s"} waiting for your call
        </p>
      </div>

      {sorted.length === 0 ? (
        <div className="border border-border rounded-xl bg-card p-10 text-center" data-testid="approvals-empty">
          <p className="text-base text-muted-foreground">Inbox zero. Nothing waiting on you.</p>
        </div>
      ) : (
        <div className="space-y-4 pb-28 md:pb-0">
          {sorted.map((t) => {
            const canApprove = t.state === "plan_ready";
            return (
              <div
                key={t.id}
                className="border border-border rounded-xl bg-card p-5 md:p-6"
                data-testid={`approval-card-${t.id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <StateBadge state={t.state} />
                      <PriorityBadge priority={t.priority} />
                      <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                        {t.type} · {t.test_scope} tests · {fmtRelative(t.updated_at)}
                      </span>
                    </div>
                    <h2 className="font-display text-xl font-bold tracking-tight">{t.title}</h2>
                    {t.plan?.summary && (
                      <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{t.plan.summary}</p>
                    )}
                  </div>
                  <Link
                    to={`/tasks/${t.id}`}
                    className="text-xs font-mono text-primary flex items-center gap-1 hover:underline"
                    data-testid={`approval-open-${t.id}`}
                  >
                    Open <ChevronRight className="h-3 w-3" />
                  </Link>
                </div>

                {t.plan?.risks?.length ? (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {t.plan.risks.map((r, i) => (
                      <span key={i} className="text-[11px] px-2 py-0.5 rounded-full border border-amber-500/30 text-amber-500 bg-amber-500/10">
                        ⚠ {r}
                      </span>
                    ))}
                  </div>
                ) : null}

                <div className="mt-4 flex flex-wrap items-center gap-4 text-xs font-mono">
                  <span>Est. <span style={{color: "hsl(var(--usd))"}}>{fmtUSD(t.plan?.estimate_usd)}</span></span>
                  <span>· <span style={{color: "hsl(var(--ecu))"}}>{fmtECU(t.plan?.estimate_ecu)}</span></span>
                  {t.assigned_agent && <span className="text-muted-foreground">· by {t.assigned_agent}</span>}
                </div>

                {t.plan?.text && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-xs font-mono text-muted-foreground hover:text-foreground">
                      View full plan
                    </summary>
                    <div className="mt-3 border-t border-border pt-3">
                      <Markdown>{t.plan.text}</Markdown>
                    </div>
                  </details>
                )}

                <div className="mt-5 flex gap-3 flex-wrap">
                  {canApprove && (
                    <>
                      <Button
                        onClick={() => triggerDecision(t.id, "approve")}
                        data-testid={`approve-btn-${t.id}`}
                        className="h-14 flex-1 md:flex-none md:h-11 md:px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base rounded-xl shadow-lg shadow-emerald-600/20 active:scale-[0.98] transition-all"
                      >
                        <CheckCircle2 className="h-5 w-5 mr-2" /> Approve
                      </Button>
                      <Button
                        onClick={() => triggerDecision(t.id, "changes")}
                        data-testid={`changes-btn-${t.id}`}
                        variant="outline"
                        className="h-14 flex-1 md:flex-none md:h-11 md:px-6 bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30 font-bold text-base rounded-xl active:scale-[0.98]"
                      >
                        <MessageSquareDiff className="h-5 w-5 mr-2" /> Request changes
                      </Button>
                    </>
                  )}
                  <Button
                    onClick={() => triggerRh(t.id, "hold")}
                    data-testid={`hold-btn-${t.id}`}
                    variant="outline"
                    className="h-14 flex-1 md:flex-none md:h-11 md:px-6 bg-zinc-500/10 hover:bg-zinc-500/20 text-foreground border border-zinc-500/30 font-bold text-base rounded-xl active:scale-[0.98]"
                  >
                    <Pause className="h-5 w-5 mr-2" /> Hold
                  </Button>
                  <Button
                    onClick={() => triggerRh(t.id, "reject")}
                    data-testid={`reject-btn-${t.id}`}
                    className="h-14 flex-1 md:flex-none md:h-11 md:px-6 bg-rose-600 hover:bg-rose-700 text-white font-bold text-base rounded-xl shadow-lg shadow-rose-600/20 active:scale-[0.98]"
                  >
                    <XCircle className="h-5 w-5 mr-2" /> Reject
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <DecisionDialog
        open={open}
        onOpenChange={setOpen}
        taskId={activeId}
        decision={decision}
        onDone={onDone}
      />
      <RejectHoldDialog
        open={rhOpen}
        onOpenChange={setRhOpen}
        taskId={activeId}
        action={rhAction}
        onDone={onDone}
      />
    </div>
  );
}
